import { Edit } from "@mui/icons-material";
import {
  Autocomplete,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  TextField,
} from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import axios from "axios";
import InputField from "components/Input/InputField";
import WholeScreenLoader from "components/WholeScreenLoader";
import { API_URL } from "const/constants";
import useKothar from "context/useKothar";
import { useEffect, useMemo, useState } from "react";
import { IoArrowBack } from "react-icons/io5";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  USER_TYPE_OPTIONS,
  buildMembershipPayload,
  createEmptyMembershipRow,
  membershipRowsFromApi,
} from "utils/userMembershipUtils";
import {
  canAccessPlatformOrganisations,
  canManagePlatformPermissionGrants,
} from "utils/permissionUtils";
import MembershipRowsEditor from "./MembershipRowsEditor";
import PermissionDrawer from "./PermissionDrawer";
import PlatformPermissionsSection from "./PlatformPermissionsSection";

const AddUsers = () => {
  const { id } = useParams();
  const [
    { consultancyList, permissionList },
    { refetchUsers, refetchPermissionList, refetchConsultancy },
  ] = useKothar();

  const [openDrawer, setOpenDrawer] = useState(false);
  const [drawerMembership, setDrawerMembership] = useState(null);
  const [membershipRows, setMembershipRows] = useState([]);
  const [platformOnlyAccount, setPlatformOnlyAccount] = useState(false);
  const [platformPermissionKeys, setPlatformPermissionKeys] = useState([]);
  const [pendingRemoveOrgId, setPendingRemoveOrgId] = useState(null);
  const permittedModules = [];
  const [data, setData] = useState({
    name: "",
    password: "",
    mobileNumber: "",
    email: "",
    username: "",
    type: "",
    accessToDiscussion: true,
    orgId: null,
    orgIds: [],
    permittedModules: permittedModules || [],
  });

  const activeOrgId = useMemo(() => {
    try {
      const detail = JSON.parse(localStorage.getItem("userDetail"));
      return detail?.activeOrgId || detail?.orgId || null;
    } catch {
      return null;
    }
  }, []);

  const orgOptions = useMemo(() => {
    const list = consultancyList || [];
    if (canAccessPlatformOrganisations()) {
      return list;
    }
    if (list.length <= 1) {
      return list;
    }
    if (activeOrgId) {
      const scoped = list.filter((item) => item.id === activeOrgId);
      return scoped.length > 0 ? scoped : list;
    }
    return list;
  }, [consultancyList, activeOrgId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setData((prevState) => ({ ...prevState, [name]: value }));
  };

  useEffect(() => {
    refetchPermissionList();
    refetchConsultancy();
  }, []);

  const { state } = useLocation();
  const navigate = useNavigate();

  const isPlatformOrgAdmin = canAccessPlatformOrganisations();
  const canManagePlatformPermissions = canManagePlatformPermissionGrants();
  const isEditing = Boolean(id || state?.item || data?.id);
  const useMembershipRows = isPlatformOrgAdmin;
  const isPlatformOnlyMode = canManagePlatformPermissions && platformOnlyAccount;

  useEffect(() => {
    if (!data?.id && activeOrgId && !data?.orgId && !isPlatformOrgAdmin) {
      setData((prev) => ({ ...prev, orgId: activeOrgId }));
    }
  }, [activeOrgId, data?.id, data?.orgId, isPlatformOrgAdmin]);

  const availableOrgs = useMemo(
    () => orgOptions.filter((org) => !membershipRows.some((row) => row.orgId === org.id)),
    [orgOptions, membershipRows]
  );

  useEffect(() => {
    if (
      !useMembershipRows ||
      isPlatformOnlyMode ||
      membershipRows.length > 0 ||
      !orgOptions.length
    ) {
      return;
    }
    const defaultOrg = orgOptions.find((item) => item.id === activeOrgId) || orgOptions[0];
    if (defaultOrg) {
      setMembershipRows([createEmptyMembershipRow(defaultOrg)]);
    }
  }, [useMembershipRows, isPlatformOnlyMode, orgOptions, activeOrgId, membershipRows.length]);

  const { isLoading: isFetchingUserData, refetch: refetchUserData } = useQuery({
    queryKey: ["userData", id],
    queryFn: async () => {
      const response = await axios.get(`${API_URL}/users/${id}`);
      const res = response.data.data;
      const membershipsResponse = await axios.get(`${API_URL}/users/${id}/memberships`);
      const memberships = membershipsResponse.data?.data || res?.memberships || [];
      const rows = membershipRowsFromApi(memberships, consultancyList || []);
      setMembershipRows(rows);
      setPlatformOnlyAccount(canManagePlatformPermissions && rows.length === 0);
      const primaryRow = rows[0];
      setData({
        name: res?.name,
        orgId:
          primaryRow?.orgId ||
          res?.orgId ||
          res?.organization?.id ||
          state?.item?.orgId ||
          state?.item?.organization?.id,
        orgIds: rows.map((row) => row.orgId),
        username: res?.username,
        mobileNumber: res?.mobileNumber,
        email: res?.email,
        password: res?.password,
        type: primaryRow?.role || res?.type,
        accessToDiscussion: res?.accessToDiscussion ?? false,
        mfa: res?.mfa,
        id: res?.id,
        membershipId: primaryRow?.membershipId || res?.membershipId,
        permittedModules: primaryRow?.permissionKeys?.length
          ? primaryRow.permissionKeys
          : res?.permissions || permittedModules,
      });
      return res;
    },
    enabled: false,
  });

  const { isLoading: isSubmitting, mutate } = useMutation(postData, {
    onSuccess(response) {
      toast.success(
        response?.message || (data?.id ? "Data updated Successfully" : "Data added Successfully")
      );
      navigate("/admin/user");
      refetchUsers();
    },
    onError(err) {
      console.log(err);
      toast.error(
        data?.id
          ? err?.response?.data?.errorMessage ?? "Error Updating Data"
          : err?.response?.data?.errorMessage ?? "Error Submitting Data"
      );
    },
  });

  async function postData(payload) {
    const membershipPayload =
      useMembershipRows && !isPlatformOnlyMode ? buildMembershipPayload(membershipRows) : [];
    const permissionKeys = (data?.permissions || data?.permittedModules || [])
      .map((item) => (typeof item === "string" ? item : item?.name))
      .filter(Boolean);

    const findUserByEmail = async (email) => {
      const usersResponse = await axios.get(`${API_URL}/users`);
      return (usersResponse.data?.data || []).find(
        (user) => user.email?.toLowerCase() === email?.toLowerCase()
      );
    };

    const savePlatformPermissions = async (userId, keys) => {
      if (!userId || !keys?.length) {
        return;
      }
      await axios.put(`${API_URL}/users/${userId}/platform-permissions/name`, {
        permissions: keys,
      });
    };

    if (payload?.id) {
      if (isPlatformOnlyMode) {
        await axios.put(`${API_URL}/users/${payload.id}/memberships`, {
          memberships: [],
        });
        await savePlatformPermissions(payload.id, platformPermissionKeys);
        return { message: "Data updated Successfully" };
      }
      const primaryOrgId = membershipPayload?.[0]?.orgId || payload.orgId;
      const updatePayload = {
        ...payload,
        orgId: primaryOrgId,
        type: useMembershipRows ? membershipPayload?.[0]?.role || payload.type : payload.type,
        permissions: useMembershipRows ? undefined : permissionKeys,
        memberships: useMembershipRows ? membershipPayload : undefined,
        orgIds: undefined,
      };
      await axios.put(`${API_URL}/users/${payload?.id}`, updatePayload);
      return { message: "Data updated Successfully" };
    }

    const primaryMembership = membershipPayload?.[0];
    const createPayload = {
      ...payload,
      orgId: isPlatformOnlyMode ? undefined : primaryMembership?.orgId || payload.orgId,
      type: isPlatformOnlyMode ? undefined : primaryMembership?.role || payload.type,
      permissions: isPlatformOnlyMode
        ? undefined
        : primaryMembership?.permissionKeys?.length
        ? primaryMembership.permissionKeys
        : permissionKeys,
    };
    const response = await axios.post(`${API_URL}/register/user`, createPayload);

    if (isPlatformOnlyMode) {
      const createdUser = await findUserByEmail(payload.email);
      await savePlatformPermissions(createdUser?.id, platformPermissionKeys);
    } else {
      let createdUser = null;
      if (useMembershipRows && membershipPayload?.length > 1) {
        createdUser = await findUserByEmail(payload.email);
        if (createdUser?.id) {
          await axios.put(`${API_URL}/users/${createdUser.id}/memberships`, {
            memberships: membershipPayload,
          });
        }
      }
      if (canManagePlatformPermissions && platformPermissionKeys.length) {
        createdUser = createdUser || (await findUserByEmail(payload.email));
        await savePlatformPermissions(createdUser?.id, platformPermissionKeys);
      }
    }
    return response.data;
  }

  const validateForm = () => {
    if (!data?.name?.trim()) {
      toast.error("Full name is required");
      return false;
    }
    if (!data?.username?.trim()) {
      toast.error("Username is required");
      return false;
    }
    if (!data?.email?.trim()) {
      toast.error("Email is required");
      return false;
    }
    if (!data?.mobileNumber) {
      toast.error("Mobile number is required");
      return false;
    }
    if (isPlatformOnlyMode) {
      if (!platformPermissionKeys.length) {
        toast.error("Select at least one platform permission for a platform-only account");
        return false;
      }
      return true;
    }
    if (useMembershipRows) {
      if (!membershipRows.length) {
        toast.error("Add at least one organisation membership");
        return false;
      }
      if (membershipRows.some((row) => !row.role)) {
        toast.error("Each organisation membership needs a user type");
        return false;
      }
      return true;
    }
    if (!data?.orgId) {
      toast.error("Select a consultancy");
      return false;
    }
    if (!data?.type) {
      toast.error("Select a user type");
      return false;
    }
    return true;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }

    const primaryRow = membershipRows[0];
    mutate({
      id: data?.id,
      type: isPlatformOnlyMode
        ? undefined
        : useMembershipRows
        ? primaryRow?.role || data?.type
        : data?.type,
      name: data?.name,
      username: data?.username,
      email: data?.email,
      ...(data?.id ? { password: data?.password } : {}),
      mfa: data?.mfa,
      mobileNumber: data?.mobileNumber,
      orgId: isPlatformOnlyMode ? undefined : useMembershipRows ? primaryRow?.orgId : data?.orgId,
      accessToDiscussion: data?.accessToDiscussion,
    });
  };

  useEffect(() => {
    if (id) {
      refetchUserData();
    } else if (state?.item) {
      refetchUserData();
    }
  }, [id, state?.item]);

  const handleOpenPermissionModule = () => {
    setDrawerMembership(null);
    setOpenDrawer(true);
  };

  const handleOpenMembershipPermissions = (row) => {
    setDrawerMembership({
      orgId: row.orgId,
      orgName: row.orgName,
      membershipId: row.membershipId,
      role: row.role,
      permissionKeys: row.permissionKeys || [],
      permissions: row.permissionKeys || [],
      permittedModules: row.permissionKeys || [],
    });
    setOpenDrawer(true);
  };

  const handleAddMembershipOrg = (org) => {
    if (!org) {
      return;
    }
    setPlatformOnlyAccount(false);
    setMembershipRows((prev) => [...prev, createEmptyMembershipRow(org)]);
  };

  const handleRemoveMembershipRow = (orgId) => {
    if (membershipRows.length <= 1) {
      if (canManagePlatformPermissions) {
        setPendingRemoveOrgId(orgId);
        return;
      }
      toast.warning("A user must belong to at least one organisation");
      return;
    }
    setMembershipRows((prev) => prev.filter((row) => row.orgId !== orgId));
  };

  const handleConfirmRemoveLastMembership = () => {
    setMembershipRows((prev) => prev.filter((row) => row.orgId !== pendingRemoveOrgId));
    setPlatformOnlyAccount(true);
    setData((prev) => ({ ...prev, orgId: null, orgIds: [], type: "" }));
    setPendingRemoveOrgId(null);
  };

  const handlePlatformOnlyChange = (event) => {
    const checked = event.target.checked;
    setPlatformOnlyAccount(checked);
    if (checked) {
      setMembershipRows([]);
      setData((prev) => ({ ...prev, orgId: null, orgIds: [], type: "" }));
    } else {
      setPlatformPermissionKeys([]);
    }
  };

  const handleMembershipRoleChange = (orgId, role) => {
    setMembershipRows((prev) => prev.map((row) => (row.orgId === orgId ? { ...row, role } : row)));
  };

  const handleMembershipPermissionsSaved = (permissionKeys) => {
    if (drawerMembership?.orgId) {
      setMembershipRows((prev) =>
        prev.map((row) => (row.orgId === drawerMembership.orgId ? { ...row, permissionKeys } : row))
      );
      setDrawerMembership(null);
      return;
    }
    setData((prev) => ({
      ...prev,
      permissions: permissionKeys,
      permittedModules: permissionKeys,
    }));
  };

  return (
    <div className="flex flex-wrap mt-4 dashBody">
      {(isFetchingUserData || isSubmitting) && <WholeScreenLoader />}
      <div className="w-full mb-12 px-4">
        <div
          className={
            "relative flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded  bg-app-surface"
          }
        >
          <div className="rounded-t mb-0 px-10 py-3 border-0">
            <div className="flex flex-wrap items-center">
              <div className="relative w-full  max-w-full flex justify-start gap-4 items-center">
                <IoArrowBack className="text-xl cursor-pointer" onClick={() => navigate(-1)} />
                <h3 className={"font-semibold text-xl text-app-text"}>Add Admins/Users</h3>
              </div>
            </div>
          </div>
          <div className="block w-full overflow-x-auto mt-8">
            <div className="flex-auto lg:px-10 py-10 pt-0">
              <form className="space-y-4 md:space-y-6" onSubmit={handleSubmit} noValidate>
                <div className="grid grid-cols-2 gap-8">
                  <div className="relative w-full mb-3">
                    <InputField
                      label="Full Name"
                      placeholder="Enter Full Name"
                      name="name"
                      required
                      type="text"
                      value={data?.name}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="relative w-full mb-3">
                    <InputField
                      label="Username"
                      placeholder="Enter Username"
                      name="username"
                      required
                      type="text"
                      value={data?.username}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="relative w-full mb-3">
                    <InputField
                      type="email"
                      placeholder="Email"
                      name="email"
                      label="Email"
                      required
                      value={data?.email}
                      onChange={handleInputChange}
                    />
                    {!isEditing && (
                      <p className="mt-2 text-sm text-app-text-secondary">
                        To add an existing user to another organisation, use the same email address.
                        Profile details are shared across organisations.
                      </p>
                    )}
                  </div>
                  <div className="relative w-full mb-3">
                    <InputField
                      type="number"
                      placeholder="Mobile Number"
                      name="mobileNumber"
                      label="Mobile Number"
                      required
                      value={data?.mobileNumber}
                      onChange={handleInputChange}
                    />
                  </div>
                  {isEditing && (
                    <div className="relative w-full mb-3">
                      <InputField
                        type="text"
                        placeholder="Password"
                        name="password"
                        label="Password"
                        value="*******************"
                        disabled
                        onChange={handleInputChange}
                      />
                      <span className="text-sm text-orange-400">
                        Password can only be changed by the user via their profile.
                      </span>
                    </div>
                  )}
                  {!isEditing && (
                    <div className="relative w-full mb-3 bg-blue-50 rounded p-3 border border-blue-200">
                      <p className="text-sm text-blue-700">
                        A temporary password will be auto-generated and sent to the user's email.
                        They will be prompted to set a new password on first login.
                      </p>
                    </div>
                  )}

                  {canManagePlatformPermissions && (
                    <div className="col-span-2">
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={platformOnlyAccount}
                            onChange={handlePlatformOnlyChange}
                          />
                        }
                        label="Platform-only account (no organization)"
                      />
                      {isPlatformOnlyMode && (
                        <p className="text-sm text-app-text-muted mt-1 ml-8">
                          Grant platform permissions below so this user can sign in without an
                          organisation membership.
                        </p>
                      )}
                    </div>
                  )}

                  {canManagePlatformPermissions && (
                    <div className="col-span-2">
                      <PlatformPermissionsSection
                        userId={data?.id}
                        value={platformPermissionKeys}
                        onChange={setPlatformPermissionKeys}
                      />
                    </div>
                  )}

                  {useMembershipRows && !isPlatformOnlyMode ? (
                    <MembershipRowsEditor
                      rows={membershipRows}
                      orgOptions={orgOptions}
                      availableOrgs={availableOrgs}
                      onAddOrg={handleAddMembershipOrg}
                      onRemoveRow={handleRemoveMembershipRow}
                      onRoleChange={handleMembershipRoleChange}
                      onOpenPermissions={handleOpenMembershipPermissions}
                    />
                  ) : !isPlatformOnlyMode ? (
                    <>
                      <div className="relative w-full mb-3">
                        <label
                          className="block uppercase text-app-text-secondary text-xs font-bold mb-2"
                          htmlFor="grid-password"
                        >
                          Select Consultancy
                        </label>
                        <Autocomplete
                          disablePortal
                          size="small"
                          value={orgOptions?.find((item) => item?.id === data?.orgId) || null}
                          options={orgOptions || []}
                          renderInput={(params) => (
                            <TextField {...params} label="Select Consultancy" />
                          )}
                          getOptionLabel={(option) => option?.name || ""}
                          isOptionEqualToValue={(options, value) => options.id === value.id}
                          onChange={(e, value) => {
                            setData((prevState) => ({
                              ...prevState,
                              orgId: value?.id,
                              orgIds: value?.id ? [value.id] : [],
                            }));
                          }}
                        />
                      </div>
                      <div className="relative w-full mb-3">
                        <label
                          className="block uppercase text-app-text-secondary text-xs font-bold mb-2"
                          htmlFor="grid-password"
                        >
                          User Type
                        </label>
                        <Autocomplete
                          disablePortal
                          size="small"
                          options={USER_TYPE_OPTIONS}
                          value={
                            USER_TYPE_OPTIONS?.find((item) => item?.value === data?.type) || null
                          }
                          renderInput={(params) => <TextField {...params} label="User Type" />}
                          isOptionEqualToValue={(options, value) => options.value === value.value}
                          onChange={(e, value) => {
                            setData((prevState) => ({
                              ...prevState,
                              type: value?.value,
                            }));
                          }}
                        />
                      </div>
                      <div className="relative w-full mb-3">
                        <div className="flex justify-between">
                          <label
                            className="block uppercase text-app-text-secondary text-xs font-bold mb-2"
                            htmlFor="grid-password"
                          >
                            Permitted Modules
                          </label>
                          <Edit className="cursor-pointer" onClick={handleOpenPermissionModule} />
                        </div>
                      </div>
                    </>
                  ) : null}
                </div>
                <div className="w-full flex justify-end mt-6 gap-4">
                  <Button variant="outlined" onClick={() => navigate(-1)} to="">
                    Go Back
                  </Button>
                  <Button variant="contained" type="submit" disabled={isSubmitting}>
                    {isSubmitting ? "Submitting..." : "Submit"}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
      {openDrawer && (
        <PermissionDrawer
          {...{
            open: openDrawer,
            setOpen: setOpenDrawer,
            data,
            setData,
            permissionList,
            refetchUsers,
            membershipContext: drawerMembership,
            onPermissionsSaved: drawerMembership ? handleMembershipPermissionsSaved : undefined,
          }}
        />
      )}
      <Dialog
        open={pendingRemoveOrgId != null}
        onClose={() => setPendingRemoveOrgId(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Remove final organisation membership?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will remove the user from all organizations. They will only be reachable through
            their platform permissions afterward.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingRemoveOrgId(null)}>Cancel</Button>
          <Button color="warning" variant="contained" onClick={handleConfirmRemoveLastMembership}>
            Remove all memberships
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default AddUsers;
